import { FormEvent, useState } from 'react'
import { Heading1 } from '@entur/typography'
import { GridContainer, GridItem } from '@entur/grid'
import { PrimaryButton } from '@entur/button'
import { DownloadIcon } from '@entur/icons'
import { BannerAlertBox, useToast } from '@entur/alert'
import { space } from '@entur/tokens'
import CompanyDropdown, { companyFormField } from '../form/company-dropdown'
import { ValidationFormField } from '../../utils/form-utils'
import { getFIPTicketsReport } from '../../api-clients/eba-clients'
import ValidatableDatePicker, { datePickerFormField } from '../form/validatable-date-picker'
import { useDownloadableApi } from '../../api-clients/client-hooks'
import FIPChooseReportDate from './fip-choose-report-date'
import { FetchState } from '../../api-clients/api-types'
import { tolLocalDateFormat } from '../../utils/date-utils'

type State = {
  useCreatedAt: string
  companyId: ValidationFormField<number | undefined>
  fromDate: ValidationFormField<Date | undefined>
  toDate: ValidationFormField<Date | undefined>
}

const initialState: State = {
  useCreatedAt: 'true',
  companyId: companyFormField(undefined, false),
  fromDate: datePickerFormField('fromDate', 'Gyldig fra', undefined, true, undefined, true),
  toDate: datePickerFormField('toDate', 'Gyldig til', undefined, true),
}

const FipReportTab = () => {
  const [state, setState] = useState(initialState)
  const [downloadResponse, download] = useDownloadableApi(getFIPTicketsReport)
  const { addToast } = useToast()

  const downloadReport = async (event: FormEvent) => {
    event?.preventDefault()
    addToast({ variant: 'info', content: 'Laster ned rapport ...' })
    download({
      query: `?format=csv&fromDate=${tolLocalDateFormat(
        state.fromDate.value || new Date()
      )}&toDate=${tolLocalDateFormat(state.toDate.value || new Date())}&useCreatedAt=${state.useCreatedAt}${
        state.companyId.value ? `&companyId=${state.companyId.value}` : ''
      }`,
      backupFileName: `fip-billetter-${tolLocalDateFormat(new Date())}.csv`,
    })
  }

  const missingParameters = !(state.fromDate.value && state.toDate.value)

  return (
    <div>
      <Heading1>Rapport for utenlandsbilletter</Heading1>

      <form data-cy="fip-report-form" id="fip_report__form" onSubmit={downloadReport}>
        <GridContainer spacing="medium">
          <GridItem small={12}>
            <FIPChooseReportDate
              useCreatedAt={state.useCreatedAt}
              update={(value: string) => setState({ ...state, useCreatedAt: value })}
            />
          </GridItem>
          <GridItem small={4}>
            <ValidatableDatePicker
              date={state.fromDate}
              update={(date) => setState({ ...state, fromDate: date })}
            />
          </GridItem>
          <GridItem small={4}>
            <ValidatableDatePicker date={state.toDate} update={(date) => setState({ ...state, toDate: date })} />
          </GridItem>
          <GridItem data-cy="company-dropdown" small={8}>
            <CompanyDropdown
              placeholder=""
              required={false}
              company={state.companyId}
              update={(value: ValidationFormField<number | undefined>) => setState({ ...state, companyId: value })}
            />
            <div style={{ fontSize: '0.7rem', paddingLeft: 20 }}>(Valgfritt)</div>
          </GridItem>
          <GridItem small={12} medium={6} large={6}>
            <PrimaryButton
              id="submit-btn"
              className="fip_report__form-button"
              data-cy="fip-report-download-csv"
              type="submit"
              disabled={missingParameters}
              loading={downloadResponse.status === FetchState.LOADING}
            >
              <DownloadIcon /> Last ned CSV
            </PrimaryButton>
          </GridItem>
        </GridContainer>
        {downloadResponse.status === FetchState.ERROR && (
          <BannerAlertBox
            variant="error"
            title="Kan ikke laste ned rapport"
            closable={true}
            style={{ marginBottom: space.large, marginTop: space.large }}
          >
            Prøv på nytt eller ta kontakt med Entur support om det vedvarer.
          </BannerAlertBox>
        )}
      </form>
    </div>
  )
}

export default FipReportTab
